import { CommonModule } from '@angular/common';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatCard } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { LinkUtil } from '../../core/model/links-uteis.model';
import { PaginationMetadata, PaginationParams } from '../../core/model/pagination.model';
import { ApiService } from '../../core/services/api.service';
import { LinkUtilService } from '../../core/services/link-util.service';
import { SaibaMaisService } from '../../core/services/saiba-mais.service';
import { AccordionLinkComponent } from '../../shared/components/accordion-link/accordion-link.component';
import { AccordionItemBasic, AccordionItemLink } from '../../shared/components/accordion/accordion-item.model';
import { AccordionComponent } from '../../shared/components/accordion/accordion.component';
import { CardIconComponent } from '../../shared/components/card-icon/card-icon.component';
import { IframeModalComponent } from './iframe-modal/iframe-modal.component';
import { Banner } from '../../core/model/banner.model';

@Component({
  selector: 'app-home',
  imports: [CommonModule, CardIconComponent, AccordionComponent, AccordionLinkComponent, MatCard],
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss',
  standalone: true
})
export class HomeComponent implements OnInit, OnDestroy {
  @ViewChild('sessaoSaibaMais') sessaoSaibaMais: ElementRef | undefined;
  @ViewChild(AccordionComponent) accordionComponent!: AccordionComponent;
  private readonly apiUrlChatBot = environment.apiUrlChatBot;

  saibaMaisItems: AccordionItemBasic[] = [];
  linksUteisItems: AccordionItemLink[] = [];

  banners: Banner[] = [];
  hasBanners = false;

  slidesDesktop = [
    'assets/home/carrosel/servico-cidadao.png',
    'assets/home/carrosel/duvida.png',
    'assets/home/carrosel/duvida-ia.png',
    'assets/home/carrosel/como-fazer.png'
  ];

  slidesMobile = [
    'assets/home/carrosel/mobile/servico-cidadao-mobile.png',
    'assets/home/carrosel/mobile/duvida-mobile.png',
    'assets/home/carrosel/mobile/duvida-ia-mobile.png',
    'assets/home/carrosel/mobile/como-fazer-mobile.png'
  ];

  slidesLinks: { image: string; link: string }[] = [];

  currentSlide = 0;
  currentSlideLink = 0;
  intervalId!: ReturnType<typeof setInterval>;
  intervalIdLink!: ReturnType<typeof setInterval>;

  window_width = window.innerWidth;
  window_height = window.innerHeight;
  slidesPerPage = 1;

  slides = this.slidesDesktop;
  url: SafeResourceUrl | null = null;

  constructor(private saibamaisService: SaibaMaisService,
    private linkUtilService: LinkUtilService,
    private apiService: ApiService,
    private router: Router,
    private dialog: MatDialog,
    private sanitizer: DomSanitizer
  ) { }

  ngOnInit(): void {
    this.listaServicoSaibaMais();
    this.listaServicoLinksUteis();
    this.loadBanners();

    this.carregarLinks().then(() => {
      this.updateSlidesForScreenSize();
    });

    window.addEventListener('resize', () => {
      this.updateSlidesForScreenSize();
    });

    this.startCarousel();
    this.startCarouselLink();
  }

  updateSlidesForScreenSize() {
    const width = window.innerWidth;

    if (width >= 1200) {
      this.slidesPerPage = 5;
    } else if (width >= 992) {
      this.slidesPerPage = 4;
    } else if (width >= 768) {
      this.slidesPerPage = 3;
    } else if (width >= 480) {
      this.slidesPerPage = 2;
    } else {
      this.slidesPerPage = 1;
    }

    this.slides = width <= 768 ? this.slidesMobile : this.slidesDesktop;
  }

  getTransform(): string {
    const slideWidth = 230;
    const offset = this.currentSlideLink * this.slidesPerPage * slideWidth;
    return `translateX(-${offset}px)`;
  }

  get slideGroups(): any[] {
    if (!this.slidesLinks?.length) return [];
    const totalGroups = Math.ceil(this.slidesLinks.length / this.slidesPerPage);
    return Array.from({ length: totalGroups });
  }

  get infiniteSlides(): { image: string; link: string }[] {
    return this.slidesLinks;
  }

  async carregarLinks(): Promise<void> {
    const params: PaginationParams = {
      page: 1,
      size: Number.MAX_SAFE_INTEGER,
      inactive: false,
    };

    try {
      const res: { data: LinkUtil[]; meta: PaginationMetadata } = await firstValueFrom(
        this.apiService.get<{ data: LinkUtil[]; meta: PaginationMetadata }>(
          'cms/links',
          params
        )
      );

      this.slidesLinks = res.data
        .filter(link => link.featured === true)
        .map(link => ({
          image: link.image_url || '',
          link: link.url
        }));

    } catch (error) {
      console.error('Erro ao carregar links do CMS:', error);
      this.slidesLinks = [];
    }
  }

  startCarouselLink(): void {
    this.intervalIdLink = setInterval(() => this.nextLink(), 3000);
  }

  openChat(): void {
    const url = this.sanitizer.bypassSecurityTrustResourceUrl(`${this.apiUrlChatBot}`);
    this.dialog.open(IframeModalComponent, {
      height: '95vh',
      minWidth: '95vw',
      data: { url }
    });
  }

  resetIntervalLink(): void {
    clearInterval(this.intervalIdLink);
    this.startCarouselLink();
  }

  nextLink(): void {
    const totalGroups = this.slideGroups.length;
    if (totalGroups === 0) return;
    this.currentSlideLink = (this.currentSlideLink + 1) % totalGroups;
  }

  goToSlideLinks(index: number) {
    this.currentSlideLink = index;
    this.resetInterval();
  }

  ngOnDestroy(): void {
    clearInterval(this.intervalId);
    clearInterval(this.intervalIdLink);
  }

  startCarousel(): void {
    this.intervalId = setInterval(() => this.next(), 3000);
  }

  resetInterval(): void {
    clearInterval(this.intervalId);
    this.startCarousel();
  }

  next(): void {
    const length = this.hasBanners ? this.banners.length : this.slides.length;
    if (length === 0) return;
    this.currentSlide = (this.currentSlide + 1) % length;
  }

  goToSlide(index: number): void {
    this.currentSlide = index;
    this.resetInterval();
  }

  navegarParaNotaTecnica() {
    this.router.navigate(['/notas-tecnicas']);
  }

  navegarParaLogin() {
    this.router.navigate(['/area-restrita-login']);
  }
  
  navegarParaVerificarAutenticidade() {
    this.router.navigate(['/verificacao-autenticidade']);
  }

  verSaibaMais() {
    if (this.sessaoSaibaMais?.nativeElement && this.accordionComponent) {
      this.sessaoSaibaMais.nativeElement.scrollIntoView({ behavior: 'smooth' });
      if (this.accordionComponent.activeIndex === null) {
        this.accordionComponent.toggle(0);
      }
    }
  }

  listaServicoSaibaMais() {
    this.saibamaisService.getAccordionItems().subscribe({
      next: (data: AccordionItemBasic[]) => {
        this.saibaMaisItems = data;
      },
      error: (error) => {
        console.error('Erro ao buscar os itens saiba mais:', error);
      }
    });
  }

  listaServicoLinksUteis() {
    this.linkUtilService.getAccordionItems().subscribe({
      next: (data: AccordionItemLink[]) => {
        this.linksUteisItems = data;
      },
      error: (error) => {
        console.error('Erro ao buscar os itens links uteis:', error);
      }
    });
  }

  onKeyup(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      this.openChat();
    }
  }

  loadBanners() {
    this.apiService.get<{ data: Banner[] }>('banners/public').subscribe({
      next: (response) => {
        this.banners = response.data;
        this.hasBanners = this.banners.length > 0;

        // Se há banners, atualiza os slides
        if (this.hasBanners) {
          this.updateSlidesForScreenSize();
        }
      },
      error: (error) => {
        console.error('Erro ao carregar banners:', error);
        this.hasBanners = false;
      }
    });
  }

  getImageUrl(imageUrl: string): string {
    try {
      // Se a URL já contém http, retorna como está
      if (imageUrl.startsWith('http')) {
        return imageUrl;
      }
      // Usar servidor de imagens na porta 3002
      const base = 'http://localhost:3002';
      return `${base}${imageUrl.startsWith('/') ? '' : '/'}${imageUrl}`;
    } catch {
      return 'assets/home/carrosel/duvida.png'; // fallback
    }
  }

  getCurrentSlideImage(): string {
    if (this.hasBanners && this.banners.length > 0) {
      const banner = this.banners[this.currentSlide % this.banners.length];
      return this.getImageUrl(banner.image_url);
    }
    return this.slides[this.currentSlide % this.slides.length];
  }

  getCurrentSlideLink(): string | null {
    if (this.hasBanners && this.banners.length > 0) {
      return this.banners[this.currentSlide % this.banners.length].link;
    }
    return null;
  }
}
